import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import './AssignItemsDialog.css'; // shared .aid-top / .aid-count popup header styles

const FORM_NAME = 'Report Recipient View';

export type RecipientKind = 'report' | 'invoice' | 'sampleReceipt';

interface RecipientRow {
  id: string;
  fullName: string;
  position?: string | null;
  email?: string | null;
  selected: boolean;
}

// Legacy Report Recipient View recipient columns: Full Name · Position · Email.
const COLUMNS: AppColumn<RecipientRow>[] = [
  { field: 'fullName', header: 'Full Name', width: 240 },
  { field: 'position', header: 'Position', width: 180 },
  { field: 'email', header: 'Email' },
];

/**
 * Report Recipient View — the "View Recipients" popup opened from a client row's Report /
 * Invoice / Sample Receipt column. Lists that client's contacts with the chosen kind's
 * recipients ticked; OK saves the ticked set (toggles the dbo.Contact delivery flag) through
 * the shared useSaveFlow ("Do you want to Save?" → save → "Saved successfully."), like every
 * other popup. Built from the shared AppDialog + GridSearch + AppDataGrid.
 */
export function ReportRecipientsDialog({ clientId, clientName, kind, kindLabel, onClose, onSaved }: {
  clientId: string;
  clientName: string;
  kind: RecipientKind;
  kindLabel: string;
  onClose: () => void;
  /** After a successful save — close and refresh the list (recipient counts). */
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [linked, setLinked] = useState<ReadonlySet<string | number>>(new Set());
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get<{ data?: RecipientRow[] }>(`/api/v1/report-recipients?clientId=${clientId}&kind=${kind}`)
      .then(({ data }) => {
        if (!alive) return;
        const list = data.data ?? [];
        const ids = new Set<string | number>(list.filter((r) => r.selected).map((r) => r.id));
        setRows(list);
        setLinked(ids);
        setSelected(new Set(ids));
      })
      .catch(() => { if (alive) notify.error('Could not load the recipients.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId, kind, notify]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.fullName} ${r.position ?? ''} ${r.email ?? ''}`.toLowerCase().includes(q));
  }, [rows, search]);

  const dirty = useMemo(() => {
    if (selected.size !== linked.size) return true;
    for (const id of selected) if (!linked.has(id)) return true;
    return false;
  }, [selected, linked]);

  // Ticks live in grid checkboxes the automatic unsaved tracker ignores (standing rule 15).
  useUnsavedGuard(dirty);

  const close = () => { void confirmDiscardIfUnsaved().then((leave) => { if (leave) onClose(); }); };

  const save = () => {
    if (!dirty) { onClose(); return; }
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        await api.post('/api/v1/report-recipients/save', {
          clientId,
          kind,
          contactIds: [...selected].map(String),
        });
      },
      onSaved,
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message;
        notify.error(msg ?? 'Could not save the recipients.');
      },
    });
  };

  return (
    <AppDialog
      open
      title={`${kindLabel} Recipients — ${clientName}`}
      width={820}
      className="dlg-center"
      maximizable
      onClose={close}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={save}>OK</button>
          <button type="button" className="ca-btn" onClick={close}>Cancel</button>
        </>
      }
    >
      <div className="aid-top">
        <span className="aid-count">{selected.size} selected</span>
        {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
      </div>
      <AppDataGrid<RecipientRow>
        rows={visible}
        columns={COLUMNS}
        getRowId={(r) => r.id}
        loading={loading}
        defaultPageSize={20}
        initialSort={{ key: 'fullName', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowClick={(r) => setSelected((s) => {
          const next = new Set(s);
          if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
          return next;
        })}
        emptyText="No contacts for this client"
      />
    </AppDialog>
  );
}
